import axios from 'axios'
import { getToken } from '../hooks/useAuth'
import { API_BASE_URL, API_TIMEOUT, DEFAULT_HEADERS, ADMIN_API_KEY } from './config'
import { reauthService } from '../utils/reauthService'

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: DEFAULT_HEADERS,
})

// Global error handler that components can set
let globalErrorHandler: ((message: string, error: any) => void) | null = null

export function setGlobalErrorHandler(handler: (message: string, error: any) => void) {
  globalErrorHandler = handler
}

// Function to get Firestore database from localStorage
function getFirestoreDatabase(): string {
  const env = localStorage.getItem('codewords_admin_environment') || 'dev'
  switch (env) {
    case 'dev':
      return 'codewords-dev'
    case 'prod':
      return 'codewords'
    case 'integration':
      return 'integration-tests'
    default:
      return 'codewords-dev'
  }
}

// Request interceptor: Add auth token and environment headers
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      // Admin site always uses GCP identity tokens
      config.headers['X-Auth-Type'] = 'gcp'
    }
    if (ADMIN_API_KEY) {
      config.headers['X-API-Key'] = ADMIN_API_KEY
    }
    // Add Firestore database header based on selected environment
    config.headers['X-Firestore-Database'] = getFirestoreDatabase()
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor: Handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status
    const endpoint = error.config?.url || 'unknown endpoint'
    const config = error.config

    // Handle 401 Unauthorized - show reauth modal instead of redirecting
    if (status === 401 && endpoint.includes('/admin/')) {
      const hasToken = sessionStorage.getItem('codewords_admin_token')

      // Only show reauth modal if we have a token (not on login page)
      if (hasToken && !window.location.pathname.includes('/admin/login')) {
        // Don't show error toast for 401 - reauth modal will handle it
        return reauthService.showReauthModal(config)
      }
    }

    // Show error in global toast for non-401 errors
    if (globalErrorHandler && status !== 401) {
      let errorMessage = `API Error (${status}): ${endpoint}\n`

      if (status === 404) {
        errorMessage += 'Endpoint not found - This feature may not be deployed yet'
      } else if (status === 500) {
        errorMessage += 'Server error - Check Cloud Function logs'
      } else {
        errorMessage += error.response?.data?.message || error.message || 'Unknown error'
      }

      globalErrorHandler(errorMessage, error)
    }

    return Promise.reject(error)
  }
)
